'use client';

import React from 'react';
import { 
  Zap, 
  Activity, 
  RefreshCcw, 
  UserCheck, 
  ShieldCheck, 
  Gem 
} from 'lucide-react';

const GOLD_ACCENT = '#faaf40';
const SHADOW_GOLD = 'rgba(250, 175, 64, 0.6)';

const coreFeatures = [
  { 
    Icon: Zap, 
    title: 'Precision EMS Sessions', 
    description: 'Scientifically calibrated sessions designed for maximum efficiency in just 20 minutes.' 
  },
  { 
    Icon: Activity, 
    title: 'Bio-Data Analysis', 
    description: 'Comprehensive tracking of your physiological response to optimize training load.' 
  },
  { 
    Icon: RefreshCcw, 
    title: 'Active Recovery Protocols', 
    description: 'Integrated recovery systems to reduce inflammation and accelerate muscle repair.' 
  },
  { 
    Icon: UserCheck, 
    title: 'Concierge Booking', 
    description: 'Priority access and seamless scheduling through our dedicated concierge service.' 
  },
  { 
    Icon: ShieldCheck, 
    title: 'Private Suite Access', 
    description: 'Train in total privacy within our medical-grade sanitized EMS sanctuary suites.' 
  },
  { 
    Icon: Gem, 
    title: 'Elite Body Conditioning', 
    description: 'Advanced massage gun therapy and hybrid sculpt techniques included in elite tiers.' 
  },
];

export default function CoreFeaturesSection() {
  return (
    <section className="py-16 sm:py-24 bg-black text-white relative overflow-hidden">
      
      {/* Background Decor */}
      <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_top,_var(--tw-gradient-stops))] from-[#1a1a1a] via-black to-black opacity-40 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Header Section */}
        <div className="text-center mb-12 sm:mb-20">
          <h2 
            className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-4 sm:mb-6 uppercase tracking-tight" 
            style={{ color: GOLD_ACCENT, textShadow: `0 0 15px ${SHADOW_GOLD}` }}
          >
            The Aura Wellness Standard
          </h2>
          <p className="text-base sm:text-lg max-w-2xl mx-auto text-gray-400 font-light leading-relaxed">
            Precision Science. Organic Flow. Every membership is designed to provide 
            total holistic care and rapid visual definition.
          </p>
        </div>

        {/* Grid Section */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {coreFeatures.map((feature) => (
            <div 
              key={feature.title}
              className="group relative p-6 sm:p-8 rounded-2xl bg-[#121212] border border-white/5 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
            >
              <div className="absolute inset-0 rounded-2xl border border-transparent group-hover:border-[#faaf40]/50 transition-colors duration-300 pointer-events-none" />

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
                
                {/* Icon Container */}
                <div 
                  className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center transition-all duration-300 group-hover:scale-110 relative"
                  style={{ 
                    backgroundColor: 'rgba(250, 175, 64, 0.1)',
                    color: GOLD_ACCENT
                  }}
                >
                  <div className="absolute inset-0 bg-[#faaf40] opacity-0 group-hover:opacity-100 rounded-xl transition-opacity duration-300" />
                  <feature.Icon 
                    className="w-6 h-6 sm:w-7 sm:h-7 relative z-10 transition-colors duration-300 group-hover:text-black" 
                    strokeWidth={1.5}
                  />
                </div>

                <div className="flex-1">
                  <h3 className="text-xl font-bold mb-2 text-white group-hover:text-[#faaf40] transition-colors duration-300">
                    {feature.title}
                  </h3>
                  <p className="text-sm sm:text-base text-gray-500 leading-relaxed group-hover:text-gray-300 transition-colors duration-300">
                    {feature.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}